import * as designTokens from '@calm/evexia-design-tokens';
import type { LegacyPath, Mode } from '@calm/evexia-design-tokens';
import { PaletteOptions } from '../index';

/*
 * Two color mechanisms coexist on the theme, on purpose:
 *
 *   1. The MUI palette (`lightPalette`/`darkPalette` below) is MUI-internal
 *      plumbing — the vocabulary MUI's own components read
 *      (`palette.primary.main`, `palette.text.body`, `action.*`, the contrast
 *      machinery). It exists because MUI requires it; it is NOT the vocabulary
 *      our call-sites should speak.
 *
 *   2. `theme.semantics` (built in `index.ts`) is the *designer's* semantic
 *      vocabulary and the surface consumer call-sites should read
 *      (`theme.semantics.Text.Heading`, etc.). It is resolved from the
 *      `LEGACY_SEMANTICS` bridge at the active mode.
 *
 * Both resolve through `resolveColor` today, so they render byte-identical
 * values. At the rebrand flip the bridge swaps to `SEMANTICS` in one place and
 * `theme.semantics` consumers are untouched — the MUI palette rebrands via its
 * own `resolveColor`-backed slots. New consumer code should read
 * `theme.semantics`, not raw `DS_COLOR.*` and not `palette.*`.
 */

export const FACEBOOK_BRAND_COLOR = '#3B5998';

/**
 * Resolve a covered legacy semantic role to its color string for the given
 * mode. Narrows resolveLegacy's `string | number` return to `string` at
 * runtime (every color twin is a string literal) so palette slots stay typed
 * `string` without an `as` cast. Shared with theme/index.ts styleOverrides.
 *
 * Any non-'light' mode resolves to the dark twin. This mirrors the pre-
 * migration reads it replaced (`mode === 'light' ? LIGHT : DARK`) and the
 * theme's own palette pick (`colorMode === 'light' ? lightPalette :
 * darkPalette`), so a stray runtime mode renders dark instead of crashing.
 * makeTheme is invoked with a `null` mode when the theme-selector experiment
 * is enrolled but no theme is stored (localStorage.getItem returns null);
 * indexing resolveLegacy by that raw value would read `undefined.$value`.
 */
export const resolveColor = (path: LegacyPath, mode: Mode): string => {
    const value = designTokens.resolveLegacy(
        path,
        mode === 'light' ? 'light' : 'dark'
    );
    if (typeof value !== 'string') {
        throw new Error(
            `Expected a color string for "${path}", received ${typeof value}`
        );
    }
    return value;
};

/** The 4 mappable gradient families rebuilt from semantic start/end stops. */
export type GradientFamily =
    | 'background/primary'
    | 'background/secondary'
    | 'foreground/primary'
    | 'foreground/secondary';

/**
 * Per-family/per-mode geometry that DS_GRADIENT bakes into its composed strings
 * but SEMANTICS does not model: the linear-gradient angle and the opacity shader
 * overlay layers. Frozen here so buildGradient stays byte-identical to today's
 * DS_GRADIENT. Shader colors are read from DS_COLOR (single source; identical
 * bytes today, and the byte-identity test guards any drift).
 */
const GRADIENT_GEOMETRY: Record<
    GradientFamily,
    { angle: Record<Mode, number>; shaders: Record<Mode, string[]> }
> = {
    'background/primary': {
        angle: { light: 180, dark: 180 },
        shaders: {
            light: [designTokens.DS_COLOR.GRADIENT_SHADER_DARK],
            dark: [],
        },
    },
    'background/secondary': {
        angle: { light: 180, dark: 180 },
        shaders: {
            light: [
                designTokens.DS_COLOR.GRADIENT_SHADER_LIGHT,
                designTokens.DS_COLOR.GRADIENT_SHADER_DARK,
            ],
            dark: [designTokens.DS_COLOR.GRADIENT_SHADER_DARK],
        },
    },
    'foreground/primary': {
        angle: { light: 135, dark: 180 },
        shaders: { light: [], dark: [] },
    },
    'foreground/secondary': {
        angle: { light: 135, dark: 180 },
        shaders: { light: [], dark: [] },
    },
};

/**
 * Compose the linear-gradient string for a mappable family by reading its two
 * semantic stop twins (start ← *_TOP, end ← *_BOTTOM) and re-applying the frozen
 * angle + shader overlay geometry above. Byte-identical to DS_GRADIENT.
 * Non-'light' modes resolve to the dark geometry/twin (mirrors resolveColor).
 */
export const buildGradient = (family: GradientFamily, mode: Mode): string => {
    const m: Mode = mode === 'light' ? 'light' : 'dark';
    const { angle, shaders } = GRADIENT_GEOMETRY[family];
    const start = resolveColor(`gradient/${family} start`, m);
    const end = resolveColor(`gradient/${family} end`, m);
    const base = `linear-gradient(${angle[m]}deg, ${start} 0%, ${end} 100%)`;
    const layers = [
        base,
        ...shaders[m].map((c) => `linear-gradient(0deg, ${c}, ${c})`),
    ];
    return layers.join(',');
};

declare module '@mui/material/styles' {
    interface Palette {
        // It should have the same shape as primary
        monochrome: Palette['primary'];
        inputLine: string;
        shadowOverlay: string;
        gradient: {
            backgroundPrimary: string;
            backgroundSecondary: string;
            iconPrimary: string;
            iconSecondary: string;
            calm: string;
            calmBrand: string;
            calmBrandAccessible: string;
        };
        mask: {
            black: {
                '4p': string;
                '12p': string;
                '20p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
            white: {
                '8p': string;
                '12p': string;
                '20p': string;
                '30p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
        };
    }

    interface PaletteOptions {
        // It should have the same shape as primary
        monochrome: PaletteOptions['primary'];
        inputLine: string;
        shadowOverlay: string;
        gradient: {
            backgroundPrimary: string;
            backgroundSecondary: string;
            iconPrimary: string;
            iconSecondary: string;
            calm: string;
            calmBrand: string;
            calmBrandAccessible: string;
        };
        mask: {
            black: {
                '4p': string;
                '12p': string;
                '20p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
            white: {
                '8p': string;
                '12p': string;
                '20p': string;
                '30p': string;
                '40p': string;
                '60p': string;
                '80p': string;
            };
        };
    }

    interface TypeText {
        sub: string;
        body: string;
    }

    interface TypeBackground {
        primary: string;
        primaryLight: string;
        secondary: string;
        secondaryLight: string;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        monochrome: true;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonPropsColorOverrides {
        monochrome: true;
    }
}

const mask = {
    black: {
        // Off the DS's 5/10/20/40/60/80 overlay scale — snap-to-scale candidates
        // for the designer sweep, so they stay on the frozen literals.
        '4p': designTokens.DS_COLOR.BLACK_4P,
        '12p': designTokens.DS_COLOR.BLACK_12P,
        '20p': designTokens.PRIMITIVES.overlay.black._20,
        '40p': designTokens.PRIMITIVES.overlay.black._40,
        '60p': designTokens.PRIMITIVES.overlay.black._60,
        '80p': designTokens.PRIMITIVES.overlay.black._80,
    },
    white: {
        // Off-scale (#FFFFFF14 / #FFFFFF1F / #FFFFFF4D), as above.
        '8p': designTokens.DS_COLOR.WHITE_8P,
        '12p': designTokens.DS_COLOR.WHITE_12P,
        '20p': designTokens.PRIMITIVES.overlay.white._20,
        '30p': designTokens.DS_COLOR.WHITE_30P,
        '40p': designTokens.PRIMITIVES.overlay.white._40,
        '60p': designTokens.PRIMITIVES.overlay.white._60,
        '80p': designTokens.PRIMITIVES.overlay.white._80,
    },
};

export const lightPalette: PaletteOptions = {
    mode: 'light',
    monochrome: {
        light: designTokens.PRIMITIVES.color.white,
        main: designTokens.PRIMITIVES.color.white,
        // color/neutral/50 (#F6F5F8) is the nearest primitive but is not
        // byte-identical to GREY (#F5F5F5), so this stays raw for the designer sweep.
        dark: designTokens.DS_COLOR.GREY,
        contrastText: resolveColor('text/default', 'light'),
    },
    text: {
        primary: resolveColor('text/heading', 'light'),
        secondary: resolveColor('text/accent', 'light'),
        disabled: resolveColor('text/disabled', 'light'),
        sub: resolveColor('text/sub', 'light'),
        body: resolveColor('text/default', 'light'),
    },
    primary: {
        main: resolveColor('interactive/primary', 'light'),
        dark: resolveColor('interactive/primary-hover', 'light'),
        light: designTokens.DS_COLOR.LIGHT_PRIMARY_LIGHT,
        contrastText: resolveColor('text/on-interactive/default', 'light'),
    },
    secondary: {
        main: resolveColor('interactive/secondary', 'light'),
        dark: resolveColor('interactive/secondary-hover', 'light'),
        light: designTokens.DS_COLOR.LIGHT_SECONDARY_LIGHT,
        contrastText: designTokens.DS_COLOR.LIGHT_SECONDARY_TEXT_CONTRAST,
    },
    error: {
        main: resolveColor('surface/feedback/error-bold', 'light'),
        dark: designTokens.DS_COLOR.LIGHT_ERROR_DARK,
        light: resolveColor('surface/feedback/error-subtle', 'light'),
        contrastText: resolveColor('text/on-feedback/error-bold', 'light'),
    },
    warning: {
        main: resolveColor('surface/feedback/warning-bold', 'light'),
        dark: designTokens.DS_COLOR.LIGHT_WARNING_DARK,
        light: resolveColor('surface/feedback/warning-subtle', 'light'),
        contrastText: resolveColor('text/on-feedback/warning-bold', 'light'),
    },
    info: {
        main: resolveColor('surface/feedback/info-bold', 'light'),
        dark: designTokens.DS_COLOR.LIGHT_INFO_DARK,
        light: resolveColor('surface/feedback/info-subtle', 'light'),
        contrastText: resolveColor('text/on-feedback/info-bold', 'light'),
    },
    success: {
        main: resolveColor('surface/feedback/success-bold', 'light'),
        dark: designTokens.DS_COLOR.LIGHT_SUCCESS_DARK,
        light: resolveColor('surface/feedback/success-subtle', 'light'),
        contrastText: resolveColor('text/on-feedback/success-bold', 'light'),
    },
    action: {
        active: resolveColor('icon/default', 'light'),
        hover: resolveColor('surface/hover', 'light'),
        selected: designTokens.DS_COLOR.LIGHT_ACTION_SELECTED,
        disabled: designTokens.DS_COLOR.LIGHT_ACTION_DISABLED,
        disabledBackground: resolveColor('interactive/disabled', 'light'),
        focus: designTokens.DS_COLOR.LIGHT_ACTION_FOCUS,
    },
    background: {
        paper: resolveColor('surface/card-solid', 'light'),
        default: resolveColor('surface/default', 'light'),
        primary: resolveColor('gradient/background/primary start', 'light'),
        primaryLight: resolveColor('gradient/background/primary end', 'light'),
        secondary: resolveColor('gradient/background/secondary start', 'light'),
        secondaryLight: resolveColor(
            'gradient/background/secondary end',
            'light'
        ),
    },
    divider: resolveColor('border/divider', 'light'),
    inputLine: resolveColor('border/input', 'light'),
    // Plain white in light / plain black in dark, with opacity applied at each
    // use site — a legibility device (drop-shadow halo over the gradient) and a
    // backdrop scrim, not elevation. No DS role models a shadow tint
    // (effects/shadows/* are shadow definitions, not colours), so the primitives
    // are the accurate mapping rather than a compromise.
    shadowOverlay: designTokens.PRIMITIVES.color.white,
    gradient: {
        backgroundPrimary: buildGradient('background/primary', 'light'),
        backgroundSecondary: buildGradient('background/secondary', 'light'),
        iconPrimary: buildGradient('foreground/primary', 'light'),
        iconSecondary: buildGradient('foreground/secondary', 'light'),
        calm: designTokens.DS_GRADIENT.CALM_HEALTH,
        calmBrand: designTokens.DS_GRADIENT.CALM_BRAND,
        calmBrandAccessible: designTokens.DS_GRADIENT.CALM_BRAND_ACCESSIBLE,
    },
    mask,
};

export const darkPalette: PaletteOptions = {
    mode: 'dark',
    monochrome: {
        light: designTokens.PRIMITIVES.color.black,
        main: designTokens.PRIMITIVES.overlay.black._80,
        dark: designTokens.DS_COLOR.GREY,
        contrastText: resolveColor('text/default', 'dark'),
    },
    text: {
        primary: resolveColor('text/heading', 'dark'),
        secondary: resolveColor('text/accent', 'dark'),
        disabled: resolveColor('text/disabled', 'dark'),
        body: resolveColor('text/default', 'dark'),
        sub: resolveColor('text/sub', 'dark'),
    },
    primary: {
        main: resolveColor('interactive/primary', 'dark'),
        dark: resolveColor('interactive/primary-hover', 'dark'),
        light: designTokens.DS_COLOR.DARK_PRIMARY_LIGHT,
        contrastText: resolveColor('text/on-interactive/default', 'dark'),
    },
    secondary: {
        main: resolveColor('interactive/secondary', 'dark'),
        dark: resolveColor('interactive/secondary-hover', 'dark'),
        light: designTokens.DS_COLOR.DARK_SECONDARY_LIGHT,
        contrastText: designTokens.DS_COLOR.DARK_SECONDARY_TEXT_CONTRAST,
    },
    error: {
        main: resolveColor('surface/feedback/error-bold', 'dark'),
        dark: designTokens.DS_COLOR.DARK_ERROR_DARK,
        light: resolveColor('surface/feedback/error-subtle', 'dark'),
        contrastText: resolveColor('text/on-feedback/error-bold', 'dark'),
    },
    warning: {
        main: resolveColor('surface/feedback/warning-bold', 'dark'),
        dark: designTokens.DS_COLOR.DARK_WARNING_DARK,
        light: resolveColor('surface/feedback/warning-subtle', 'dark'),
        contrastText: resolveColor('text/on-feedback/warning-bold', 'dark'),
    },
    info: {
        main: resolveColor('surface/feedback/info-bold', 'dark'),
        dark: designTokens.DS_COLOR.DARK_INFO_DARK,
        light: resolveColor('surface/feedback/info-subtle', 'dark'),
        contrastText: resolveColor('text/on-feedback/info-bold', 'dark'),
    },
    success: {
        main: resolveColor('surface/feedback/success-bold', 'dark'),
        dark: designTokens.DS_COLOR.DARK_SUCCESS_DARK,
        light: resolveColor('surface/feedback/success-subtle', 'dark'),
        contrastText: resolveColor('text/on-feedback/success-bold', 'dark'),
    },
    action: {
        active: resolveColor('icon/default', 'dark'),
        hover: resolveColor('surface/hover', 'dark'),
        selected: designTokens.DS_COLOR.DARK_ACTION_SELECTED,
        disabled: designTokens.DS_COLOR.DARK_ACTION_DISABLED,
        disabledBackground: resolveColor('interactive/disabled', 'dark'),
        focus: designTokens.DS_COLOR.DARK_ACTION_FOCUS,
    },
    background: {
        paper: resolveColor('surface/card-solid', 'dark'),
        default: resolveColor('surface/default', 'dark'),
        primary: resolveColor('gradient/background/primary start', 'dark'),
        primaryLight: resolveColor('gradient/background/primary end', 'dark'),
        secondary: resolveColor('gradient/background/secondary start', 'dark'),
        secondaryLight: resolveColor(
            'gradient/background/secondary end',
            'dark'
        ),
    },
    gradient: {
        backgroundPrimary: buildGradient('background/primary', 'dark'),
        backgroundSecondary: buildGradient('background/secondary', 'dark'),
        iconPrimary: buildGradient('foreground/primary', 'dark'),
        iconSecondary: buildGradient('foreground/secondary', 'dark'),
        calm: designTokens.DS_GRADIENT.CALM_HEALTH,
        calmBrand: designTokens.DS_GRADIENT.CALM_BRAND,
        calmBrandAccessible: designTokens.DS_GRADIENT.CALM_BRAND_ACCESSIBLE,
    },
    divider: resolveColor('border/divider', 'dark'),
    inputLine: resolveColor('border/input', 'dark'),
    shadowOverlay: designTokens.PRIMITIVES.color.black,
    mask,
};
